import { useState, useRef, useEffect } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { testimonials } from "@/mocks/testimonials";

export default function Testimonials() {
  const sectionRef = useScrollReveal<HTMLElement>();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll, { passive: true });
    checkScroll();
    return () => el.removeEventListener("scroll", checkScroll);
  }, []);

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.85;
    el.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  if (!testimonials.length) return null;

  return (
    <section className="bg-zayan-bg border-t border-zayan-border py-16 md:py-20 lg:py-24 reveal" ref={sectionRef}>
      <div className="px-6 lg:px-16 mb-10 md:mb-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="text-zayan-gold text-xs tracking-[0.2em] uppercase mb-3 font-sans">
              Client Stories
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-zayan-gold leading-tight">
              What Our Clients Say
            </h2>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              className={`w-10 h-10 md:w-12 md:h-12 border flex items-center justify-center transition-colors ${
                canScrollLeft
                  ? "border-zayan-gold text-zayan-gold hover:bg-zayan-gold hover:text-zayan-bg"
                  : "border-zayan-border text-zayan-textDarker cursor-default"
              }`}
              aria-label="Previous testimonials"
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <button
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              className={`w-10 h-10 md:w-12 md:h-12 border flex items-center justify-center transition-colors ${
                canScrollRight
                  ? "border-zayan-gold text-zayan-gold hover:bg-zayan-gold hover:text-zayan-bg"
                  : "border-zayan-border text-zayan-textDarker cursor-default"
              }`}
              aria-label="Next testimonials"
            >
              <i className="ri-arrow-right-line text-lg" />
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable Cards */}
      <div
        ref={scrollRef}
        className="flex gap-5 md:gap-6 overflow-x-auto px-6 lg:px-16 pb-4 snap-x snap-mandatory scrollbar-hide"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {testimonials.map((t) => (
          <div
            key={t.id}
            className="snap-start shrink-0 w-[340px] sm:w-[380px] md:w-[420px] bg-zayan-bg border border-zayan-border p-6 md:p-8 flex flex-col group hover:border-zayan-gold/30 transition-colors"
          >
            {/* Quote Icon */}
            <i className="ri-double-quotes-l text-zayan-gold/40 text-3xl mb-4" />

            {/* Stars */}
            <div className="flex gap-1 mb-4">
              {Array.from({ length: t.rating }).map((_, i) => (
                <i key={i} className="ri-star-fill text-zayan-gold text-xs" />
              ))}
            </div>

            {/* Review Text */}
            <p className="text-zayan-textMuted text-sm leading-relaxed mb-6 flex-1">
              {t.text}
            </p>

            {/* Product Tag */}
            <p className="text-zayan-gold/70 text-[11px] tracking-wider uppercase mb-5">
              {t.product}
            </p>

            {/* Divider */}
            <div className="h-px w-full bg-zayan-border mb-5" />

            {/* Author Row */}
            <div className="flex items-center gap-3">
              {/* Avatar */}
              <div className="w-10 h-10 rounded-full bg-zayan-gold/10 border border-zayan-gold/20 flex items-center justify-center shrink-0">
                <span className="text-zayan-gold text-xs font-medium tracking-wide">
                  {t.avatar}
                </span>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-zayan-text text-sm font-medium truncate">
                    {t.name}
                  </p>
                  {t.verified && (
                    <span className="shrink-0 inline-flex items-center gap-1 text-[10px] text-zayan-textDarker tracking-wider uppercase border border-zayan-border px-2 py-0.5">
                      <i className="ri-check-line text-zayan-gold" />
                      Verified
                    </span>
                  )}
                </div>
                <p className="text-zayan-textDarker text-xs">
                  {t.location} · {t.date}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}